import React, { useEffect, useState } from "react";
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import {  yupResolver } from '@hookform/resolvers/yup';
import { usePostJobMutation } from "../../../../features/jobs/jobsApiSlice";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../../../features/auth/authSlice";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CategorySelect from "./CategorySelect";
import LocationsAutocomplete from "../../../Jobs/FiltersSidebar/LocationsAutocomplete";
import CurrencySelect from "./CurrencySelect";
import NotificationDialog from './../../../HeaderContainer/Popup/NotificationDialog';
import { INPUTS_SHADOW_STYLES } from "../../../../animations/selectDetails";
import { isValidFile, imageFormats, MAX_FILE_SIZE, MAX_JOB_PHOTOS } from "../../../../utils/fileValidationFunctions";
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

const schema = yup
  .object()
  .shape({
      description: yup.string().trim().required('JobDescriptionRequired'),
      photos: yup.mixed()
        //   .test("fileSize", "File Size is too large", (value) => {
        //       return value.length && value[0].size <= 5242880;
        //   })
        //   .test("fileType", "Unsupported File Format", (value) => {
        //       return value.length && ["image/jpeg", "image/png", "image/jpg"].includes(value[0].type)
        //   })
  });

let JobPosting = ({ isMapApiLoaded }) => {
    const { t } = useTranslation();
    const [notificationText, setNotificationText] = useState('');
    const [minP, setMinP] = useState(100);
    const [maxP, setMaxP] = useState(1000);
    const [jobDateTime, setJobDateTime] = useState(undefined);
    const [isOpen, setIsOpen] = useState(false);
    const { register, handleSubmit } = useForm({ resolver: yupResolver(schema) });
    const [selectedPhotos, setSelectedPhotos] = useState([]);
    const [photoError, setPhotoError] = useState('');

    const onPhotosSelected = event => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (selectedPhotos.length + files.length > MAX_JOB_PHOTOS) {
            setPhotoError(t('MaximumJobPhotos', { count: MAX_JOB_PHOTOS }));
            return;
        }
        for (const file of files) {
            const validation = isValidFile(file);
            if (!validation.isValid) {
                setPhotoError(t(validation.error));
                return;
            }
        }
        setPhotoError('');
        setSelectedPhotos(current => [...current, ...files.map(file => ({
            id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
            file,
            preview: URL.createObjectURL(file)
        }))]);
    };

    const removePhoto = id => {
        setSelectedPhotos(current => {
            const removed = current.find(photo => photo.id === id);
            if (removed) URL.revokeObjectURL(removed.preview);
            return current.filter(photo => photo.id !== id);
        });
        setPhotoError('');
    };

    useEffect(() => {
        let minTmp = minP == '-0' ? 0 : parseFloat(minP);
        let maxTmp = maxP == '-0' ? 0 :  parseFloat(maxP);
        if (minTmp < 0) {
            minTmp = 0;
        }

        if (maxTmp < 0) {
            maxTmp = 0;
        }

        if (minTmp && maxTmp) {
            if (minTmp >= maxTmp) {
                minTmp = maxTmp - 1;
            } 
            
            if (minTmp < 0) {
                minTmp = 0;        
            } 
            
            if (maxTmp <= 0) {
                maxTmp = minTmp + 1;
            }
        }

        if (minTmp === maxTmp) {
            maxTmp++;
        }

        setMinP(minTmp);
        setMaxP(maxTmp);
    }, [minP, maxP])
    
    const navigate = useNavigate();
    const user = useSelector(selectCurrentUser);
    const [postJob, { isLoading: isPosting }] = usePostJobMutation();

    const [categoryFK, setCategoryFK] = useState(1);
    const [jobLocation, setJobLocation] = useState({});
    const [currencyFK, setCurrencyFK] = useState(1);

    const onSubmit = async data => {
        if (!Number(user?.isEmailVerified)) {
            setNotificationText('EmailVerificationRequired');
            setIsOpen(true);
            return;
        }
        try {
            if (minP == 0 || !minP || !maxP || maxP == 0 || (minP >= maxP) || isNaN(minP) || isNaN(maxP)) {
                setNotificationText('NotValidPaymentData');
                setIsOpen(true);
                return;
            }

            if (!jobDateTime) {
                setNotificationText('EmptyJobDateTime');
                setIsOpen(true);
                return;
            }

            if (!Number.isFinite(jobLocation?.lat) || !Number.isFinite(jobLocation?.lng)) {
                setNotificationText('JobLocationRequired');
                setIsOpen(true);
                return;
            }

            if (selectedPhotos.length === 0) {
                setNotificationText('JobPhotoRequired');
                setIsOpen(true);
                return;
            }

            let formData = new FormData();
            selectedPhotos.forEach((photo, index) => formData.append(`photo_${index}`, photo.file));

            let jobPostData = {
                clientFK: user.id,
                ...data,
                categoryFK,
                minPayment: minP,
                maxPayment: maxP,
                currencyFK,
                jobDateTime,
                ...jobLocation
            };
            Object.keys(jobPostData).forEach(key => 
                formData.append(key, jobPostData[key])
            );
            
            // let formEntries = Array.from(formData.entries());
            // console.log("formEntries " , formEntries); 

            const jobData = await postJob(formData).unwrap();
            if (!jobData?.success) throw new Error('JobPublishFailed');

            navigate('/client-office/manage-jobs', 
            { 
                replace: true,
                state: {name: 'ManageJobs', page: 'ManageJobs'} 
            },
            );
            
        } catch (error) {
            console.error(error);
            const responseMessage = error?.data?.message;
            if (responseMessage === 'EmailVerificationRequired') {
                setNotificationText('EmailVerificationRequired');
            } else if (error?.status === 400 || error?.status === 413 || error?.status === 422) {
                setNotificationText('JobPublishInvalidData');
            } else {
                setNotificationText('JobPublishFailed');
            }
            setIsOpen(true);
        }
    };

    const onInvalid = errors => {
        const firstError = Object.values(errors || {})[0];
        setNotificationText(firstError?.message || 'JobPublishInvalidData');
        setIsOpen(true);
    };

    return <div className="row">

        {/*encType="multipart/form-data"*/}
        <form onSubmit={handleSubmit(onSubmit, onInvalid)} method="post" id="postJobForm">
            <div className="col-xl-12">
                <div className="dashboard-box margin-top-0">
    
                    {/* <!-- Headline --> */}
                    <div className="headline">
                        <h3><i className="icon-feather-folder-plus"></i> {t('JobSubmissionForm')}</h3>
                    </div>
    
                    <div className="content with-padding padding-bottom-10">
                        <div className="row">
    
                            <div className="col-xl-4">
                                <div className="submit-field">
                                    <h5>{t('WhatSpecialistDoYouNeed')}</h5>
                                    <CategorySelect setCategoryFK={setCategoryFK} />
                                </div>
                            </div>
    
                            <div className="col-xl-4">
                                <div className="submit-field">
                                    <h5>{t('Location')}</h5>
                                    {isMapApiLoaded && <LocationsAutocomplete setCenter={setJobLocation}
                                        onLocationChange={location => setJobLocation(current => ({
                                            ...current,
                                            locationLabel: location?.label || ''
                                        }))} />}
                                </div>
                            </div>
                            
                            <div className="col-xl-4">
                                <div className="submit-field">
                                    <h5>{t('DateAndTime')}</h5>
                                    <input style={INPUTS_SHADOW_STYLES}
                                    name="jobDateTime"
                                    value={jobDateTime}
                                    onChange={e => setJobDateTime(e.target.value)}
                                    //{...register('jobDateTime', { required: true })}
                                    type={'datetime-local'} />
                                </div>
                            </div>

                            <div className="col-xl-4">
                                <div className="submit-field">
                                    <h5>{t('MinPayment')}</h5>
                                    <input style={INPUTS_SHADOW_STYLES}
                                    name="minPayment" 
                                    value={minP}
                                    onChange={e => setMinP(e.target.value)} 
                                    type="number"
                                    autoComplete="off" />
                                </div>
                            </div>

                            <div className="col-xl-4">
                                <div className="submit-field">
                                    <h5>{t('MaxPayment')}</h5>
                                    <input style={INPUTS_SHADOW_STYLES}
                                    name="maxPayment" 
                                    value={maxP}
                                    onChange={e => setMaxP(e.target.value)} 
                                    type="number"
                                    autoComplete="off" />
                                </div>
                            </div>

                            <div className="col-xl-4">
                                <div className="submit-field">
                                    <h5>{t('Currency')}</h5>
                                    <CurrencySelect setCurrencyFK={setCurrencyFK} />
                                </div>
                            </div>

                            <div className="col-xl-12">
                                <div className="submit-field">
                                    <h5>{t('JobDescription')}</h5>
                                    <textarea style={INPUTS_SHADOW_STYLES}
                                    name="description"
                                    {...register('description')}
                                     cols="30" rows="5" className="with-border"></textarea>
                                     
                                     <p className="margin-top-30" title="explanation" style={{ cursor: 'pointer', marginBottom: 8 }} >
                                        <i className="icon-feather-info"></i>
                                        &nbsp;{t('PhotoUploadConstraints', { count: MAX_JOB_PHOTOS, size: MAX_FILE_SIZE })}&nbsp;
                                        <i>{imageFormats.join(', ')}.</i>
                                    </p>
                                    <div className="uploadButton">
                                        <input name="photos" onChange={onPhotosSelected}
                                            className="uploadButton-input" type="file" accept={imageFormats.join(',')} id="upload" multiple />
                                        <label className="uploadButton-button ripple-effect" htmlFor="upload">{t('UploadPhotos')}</label>
                                        <span className="uploadButton-file-name">
                                            {t('SelectedPhotosCount', { selected: selectedPhotos.length, count: MAX_JOB_PHOTOS })}
                                        </span>
                                    </div>
                                    {photoError && <div role="alert" style={{ color: '#b42318', marginTop: 8 }}>{photoError}</div>}
                                    {selectedPhotos.length > 0 && <PhotoProvider>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
                                            {selectedPhotos.map((photo, index) => <div key={photo.id} style={{ position: 'relative' }}>
                                                <PhotoView src={photo.preview}>
                                                    <img src={photo.preview} alt={`${t('JobPhoto')} ${index + 1}`}
                                                        style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 4, cursor: 'zoom-in' }} />
                                                </PhotoView>
                                                <button type="button" onClick={() => removePhoto(photo.id)}
                                                    aria-label={t('RemovePhoto')}
                                                    style={{ position: 'absolute', top: -8, right: -8, width: 26, height: 26,
                                                        padding: 0, border: 0, borderRadius: '50%', background: '#d92d20', color: '#fff',
                                                        fontSize: 18, lineHeight: '26px', cursor: 'pointer' }}>×</button>
                                            </div>)}
                                        </div>
                                    </PhotoProvider>}
                                </div>
                            </div>
    
                        </div>
                    </div>
                </div>
            </div>
    
            <div className="col-xl-12">
                <button type="submit"
                form="postJobForm"
                disabled={isPosting}
                aria-busy={isPosting}
                className="button ripple-effect big margin-top-30"
                style={isPosting ? { opacity: 0.7, cursor: 'wait' } : undefined}>
                    <i className={isPosting ? "icon-feather-loader" : "icon-feather-plus"}></i>{' '}
                    {isPosting ? t('PublishingJob') : t('PostAJob')}
                </button>
            </div>
        </form>


        <NotificationDialog type="warning" open={isOpen} onClose={() => setIsOpen(false)}>
            {t(notificationText)}
        </NotificationDialog>
    </div>
;
}

export default JobPosting;
